import { apiGet, apiPost, apiPut, apiDelete, planningPath } from './PlanningApiClient';
import { PROCUREMENT_ENABLED } from '../constants/featureFlags';
import type {
  ApiEnvelope, FreightAllocation, LandedCost, LineInput, LineRow, ListMeta, OrderDetail, OrderInput, OrderRow,
  ConfirmReceiptInput, ConfirmReceiptResult, OrderShipment, ParcelRow, ReadyShipmentSummary, ShipmentReadyToReceive, ProcurementSummary, ReceiveInput, ReceivingView, ShipmentDetail, ShipmentInput, ShipmentRow,
} from '../types/Procurement';

/**
 * Procurement API (orders, lines, receiving, parcels, shipments).
 * Every path is under /planning/:project_id/procurement and answers in the
 * envelope { success, data, meta }. Ids are uuid strings and are only ever
 * encoded into the path, never parsed. Money stays a decimal string.
 * Nothing here swallows an error: a screen decides how to say it failed.
 */

const base = '/procurement';
const enc = encodeURIComponent;

export type Lifecycle = 'active' | 'archived' | 'all';

const query = (params: Record<string, string | number | undefined | null>): string => {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
  const s = usp.toString();
  return s ? `?${s}` : '';
};

async function unwrap<T>(promise: Promise<ApiEnvelope<T>>): Promise<T> {
  const res = await promise;
  if (!res || res.success === false) throw new Error(res?.error || 'The request did not go through.');
  return res.data;
}

const PAGE_LIMIT = 100;
const MAX_PAGES = 20;

/** Reads every page of a list (the API caps a page at 100) so filters and search work on the whole set. */
async function fetchAllPages<T>(path: string, params: Record<string, string | number | undefined | null>): Promise<T[]> {
  const out: T[] = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const res = await apiGet<ApiEnvelope<T[]>>(planningPath(`${path}${query({ ...params, page, limit: PAGE_LIMIT })}`));
    if (!res || res.success === false) throw new Error(res?.error || 'The list could not be loaded.');
    const rows = Array.isArray(res.data) ? res.data : [];
    out.push(...rows);
    const meta: ListMeta | undefined = res.meta;
    if (rows.length < PAGE_LIMIT || !meta || out.length >= meta.total) break;
  }
  return out;
}

// ── Orders ──────────────────────────────────────────────────────────────────

export const fetchOrders = (opts: { lifecycle?: Lifecycle; status?: string; shipment_id?: string } = {}) =>
  fetchAllPages<OrderRow>(`${base}/orders`, { lifecycle: opts.lifecycle, status: opts.status, shipment_id: opts.shipment_id });

export const fetchProcurementSummary = () =>
  unwrap(apiGet<ApiEnvelope<ProcurementSummary>>(planningPath(`${base}/summary`)));

export const fetchOrder = (id: string) =>
  unwrap(apiGet<ApiEnvelope<OrderDetail>>(planningPath(`${base}/orders/${enc(id)}`)));

export const createOrder = (input: OrderInput) =>
  unwrap(apiPost<ApiEnvelope<OrderRow>>(planningPath(`${base}/orders`), input));

export const updateOrder = (id: string, input: OrderInput) =>
  unwrap(apiPut<ApiEnvelope<OrderRow>>(planningPath(`${base}/orders/${enc(id)}`), input));

export const archiveOrder = (id: string) =>
  unwrap(apiPost<ApiEnvelope<OrderRow>>(planningPath(`${base}/orders/${enc(id)}/archive`), {}));

export const restoreOrder = (id: string) =>
  unwrap(apiPost<ApiEnvelope<OrderRow>>(planningPath(`${base}/orders/${enc(id)}/restore`), {}));

// ── Lines ───────────────────────────────────────────────────────────────────

export const addOrderLine = (orderId: string, input: LineInput) =>
  unwrap(apiPost<ApiEnvelope<LineRow>>(planningPath(`${base}/orders/${enc(orderId)}/items`), input));

export const updateOrderLine = (orderId: string, itemId: string, input: Partial<LineInput>) =>
  unwrap(apiPut<ApiEnvelope<LineRow>>(planningPath(`${base}/orders/${enc(orderId)}/items/${enc(itemId)}`), input));

export async function deleteOrderLine(orderId: string, itemId: string): Promise<void> {
  await apiDelete(planningPath(`${base}/orders/${enc(orderId)}/items/${enc(itemId)}`));
}

// ── Receiving ───────────────────────────────────────────────────────────────

export const fetchReceiving = (orderId: string) =>
  unwrap(apiGet<ApiEnvelope<ReceivingView>>(planningPath(`${base}/orders/${enc(orderId)}/receiving`)));

export const receiveGoods = (orderId: string, input: ReceiveInput) =>
  unwrap(apiPost<ApiEnvelope<ReceivingView>>(planningPath(`${base}/orders/${enc(orderId)}/receive`), input));

// ── Parcels ─────────────────────────────────────────────────────────────────

export const addParcel = (orderId: string, input: { tracking_no: string; courier?: string }) =>
  unwrap(apiPost<ApiEnvelope<ParcelRow>>(planningPath(`${base}/orders/${enc(orderId)}/tracking`), input));

// ── Order <-> shipments ─────────────────────────────────────────────────────

export const fetchOrderShipments = (orderId: string) =>
  unwrap(apiGet<ApiEnvelope<OrderShipment[]>>(planningPath(`${base}/orders/${enc(orderId)}/shipments`)));

export const createOrderShipment = (orderId: string, input: ShipmentInput & { cbm?: string | null; carton_count?: number | null }) =>
  unwrap(apiPost<ApiEnvelope<ShipmentRow>>(planningPath(`${base}/orders/${enc(orderId)}/shipments`), input));

export const attachShipment = (orderId: string, shipmentId: string, input: { cbm?: string | null; carton_count?: number | null } = {}) =>
  unwrap(apiPost<ApiEnvelope<unknown>>(planningPath(`${base}/orders/${enc(orderId)}/shipments/${enc(shipmentId)}/attach`), input));

export async function detachShipment(orderId: string, shipmentId: string): Promise<void> {
  await apiDelete(planningPath(`${base}/orders/${enc(orderId)}/shipments/${enc(shipmentId)}`));
}

// ── Landed cost (money; a contractor gets 403) ──────────────────────────────

export const fetchLandedCost = (orderId: string) =>
  unwrap(apiGet<ApiEnvelope<LandedCost | null>>(planningPath(`${base}/orders/${enc(orderId)}/landed-cost`)));

// ── Shipments ───────────────────────────────────────────────────────────────

export const fetchShipments = (opts: { status?: string; leg?: string; mode?: string; order_id?: string } = {}) =>
  fetchAllPages<ShipmentRow>(`${base}/shipments`, opts);

export const fetchShipment = (id: string) =>
  unwrap(apiGet<ApiEnvelope<ShipmentDetail>>(planningPath(`${base}/shipments/${enc(id)}`)));

export const createShipment = (input: ShipmentInput) =>
  unwrap(apiPost<ApiEnvelope<ShipmentRow>>(planningPath(`${base}/shipments`), input));

export const updateShipment = (id: string, input: ShipmentInput) =>
  unwrap(apiPut<ApiEnvelope<ShipmentRow>>(planningPath(`${base}/shipments/${enc(id)}`), input));

/** Cancelling is a status change: shipments are never hard-deleted. */
export const cancelShipment = (id: string) => updateShipment(id, { status: 'cancelled' });

/** Reactivating a cancelled shipment moves it back to draft. */
export const reactivateShipment = (id: string) => updateShipment(id, { status: 'draft' });

export const saveWaybill = (input: { tracking_no: string; courier?: string; order_id?: string; shipment_id?: string }) =>
  unwrap(apiPost<ApiEnvelope<ShipmentRow>>(planningPath(`${base}/shipments/tracking`), input));

export const fetchFreightAllocation = (shipmentId: string) =>
  unwrap(apiGet<ApiEnvelope<FreightAllocation>>(planningPath(`${base}/shipments/${enc(shipmentId)}/freight-allocation`)));

// ── Ready to receive (an arrived shipment's outstanding lines) ───────────────

export const fetchShipmentsReadyToReceive = () =>
  unwrap(apiGet<ApiEnvelope<ReadyShipmentSummary[]>>(planningPath(`${base}/shipments/ready-to-receive`)));

export const fetchShipmentReadyToReceive = (shipmentId: string) =>
  unwrap(apiGet<ApiEnvelope<ShipmentReadyToReceive>>(planningPath(`${base}/shipments/${enc(shipmentId)}/ready-to-receive`)));

/** One transaction for every order on the shipment: all lines are received, or none. */
export const confirmShipmentReceipt = (shipmentId: string, input: ConfirmReceiptInput) =>
  unwrap(apiPost<ApiEnvelope<ConfirmReceiptResult>>(planningPath(`${base}/shipments/${enc(shipmentId)}/confirm-receipt`), input));

// ── Background refresh ──────────────────────────────────────────────────────

export const PROCUREMENT_REFRESHED_EVENT = 'procurement:refreshed';

/**
 * The auto-refresh's loader: re-reads the summary and the active lists and tells
 * any open Procurement screen to re-read. It rejects on failure so the sync hook
 * can log it; it never shows a toast (only a person's own load or save does).
 */
export async function refreshProcurementFromApi(): Promise<void> {
  if (!PROCUREMENT_ENABLED) return;
  const [summary, orders, shipments] = await Promise.all([
    fetchProcurementSummary(),
    fetchOrders({ lifecycle: 'active' }),
    fetchShipments(),
  ]);
  window.dispatchEvent(new CustomEvent(PROCUREMENT_REFRESHED_EVENT, { detail: { summary, orders, shipments } }));
}
